import type { FC, PropsWithChildren } from "react";
import { useParams } from "react-router-dom";
import type { SWRConfiguration } from "swr";

import type { GetTrialInstitutionIdentityByIdResponse } from "src/api/Tmd.dtos";
import TrialInstitutionIdentityContext from "src/contexts/data/TrialInstitutionIdentityContext";
import useTrialInstitutionIdentityById from "src/hooks/data/load/useTrialInstitutionIdentityById";

/**
 * Configures the trial institution identity request and the descendants receiving its state.
 */
interface ITrialInstitutionProviderProps extends PropsWithChildren {
    id?: number;
    configuration?: SWRConfiguration<
        GetTrialInstitutionIdentityByIdResponse,
        GetTrialInstitutionIdentityByIdResponse
    >;
}

/**
 * Publishes the trial institution identity loader state to descendants.
 *
 * Postconditions: Children receive the current loader result, including loading and error state;
 * rendering this provider does not guarantee a completed request. Children remain rendered while
 * loading or on failure. The supplied ID and SWR configuration pass through unchanged.
 */
const TrialInstitutionIdentityProvider: FC<ITrialInstitutionProviderProps> = ({
    children,
    id,
    configuration
}) => {
    const context = useTrialInstitutionIdentityById(id, configuration);

    return (
        <TrialInstitutionIdentityContext.Provider value={context}>
            {children}
        </TrialInstitutionIdentityContext.Provider>
    );
};

/**
 * Loads trial institution identity from the matching route parameter for descendants.
 *
 * Preconditions: Render within the router that supplies the route parameters.
 * Postconditions: An absent or empty parameter becomes undefined; otherwise base-10 parseInt
 * supplies the ID, accepting numeric prefixes and producing NaN for unparseable text.
 * Loading and errors remain in context; this wrapper does not gate children or enforce access.
 * Assumptions: The loader handles request state and the server enforces authorization.
 */
export const RouteTrialInstitutionIdentityProvider: FC<PropsWithChildren> = ({ children }) => {
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const id = trialInstitutionId ? parseInt(trialInstitutionId, 10) : undefined;

    return <TrialInstitutionIdentityProvider id={id}>{children}</TrialInstitutionIdentityProvider>;
};

export default TrialInstitutionIdentityProvider;
